import { authService } from '@aglenergy/agl.sales.authentication';
import { SessionStorageKeys } from '@aglenergy/shared-library';
import { ObservableMap } from '@stencil/store';
import { dispatchResetCartEvent } from 'app/bundle/store/handlers/cart.handler';
import { bundleSearchService } from 'app/bundle/services/bundle-search.service';
import { State } from 'app/shared/store/models/state.model';
import { removeHandler } from 'app/shared/store/handlers/types/remove-handler';
import { AddressDetailModel, BundleSearchResponse, BundleSearchStore, CustomerType, ProductCategory } from 'app/shared/models/index';
import { delayMessageService } from 'app/shared/services/delay-message/delay-message.service';
import { localStorageService } from 'app/shared/services/local-storage/local-storage.service';
import { locationService } from 'app/shared/services/location/location-service';
import { energyRequestMapper } from 'app/energy/mappers/energy-request.mapper';
import { SearchRequestType } from 'app/shared/models/energy/energy.enums';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { addressService } from 'app/shared/services/address/address.service';
import { bundleService } from 'app/shared/services/bundle/bundle.service';
import { abandonCart } from 'app/shared/services/cart/cart.service';

const isAddressChanged = (store: ObservableMap<State>): boolean => {
  const storeAddress = store.get('address');
  const bundleSearchStore = store.get(StoreKeys.bundleSearchStore);
  const addressString = addressService.convertAddressToString(storeAddress);
  const bundleStoreAddress = bundleSearchStore?.request?.addressString;
  return bundleStoreAddress && addressString && bundleStoreAddress !== addressString;
};

export const addAddressHandler = (store: ObservableMap<State>, isBundleLandingPage = false): removeHandler => {
  return store.onChange('address', async (address) => {
    // Reset cart in store when address is changed
    const firstLoad = !isBundleLandingPage && !sessionStorage.getItem(SessionStorageKeys.bundleProducts);
    if (firstLoad) {
      localStorageService.setRegion(address.state);
    }
    const addressChanged = isAddressChanged(store);
    if (addressChanged) {
      abandonCart();
      store.set(StoreKeys.responseError, null);
      localStorageService.setRegion(address.state);
      dispatchResetCartEvent();
    }
    await searchProductAddress(store, address, isBundleLandingPage).then(() => {
      const addressInStorage = window.sessionStorage.getItem(SessionStorageKeys.address);
      if (addressChanged || firstLoad || (!addressChanged && !isBundleLandingPage && !addressInStorage)) {
        window.location.reload();
      } else if (isBundleLandingPage && !store.get(StoreKeys.responseError)) {
        locationService.navigateToProductBundle();
      }
    });
  });
};

export const searchProductAddress = async (
  store: ObservableMap<State>,
  address: AddressDetailModel,
  isBundleLandingPage: boolean
): Promise<void> => {
  delayMessageService.startAllTimers();
  const productCategories: ProductCategory[] = ['energy', 'nbn', 'mobile plan'];
  const searchRequest = energyRequestMapper.mapToCurrentSearchRequestModel(
    address,
    CustomerType.residential,
    productCategories,
    SearchRequestType.manualAddress
  );
  const bundleProductSearchStore: BundleSearchStore = bundleSearchService.loadProductsResponseFromStore(searchRequest);
  if (!bundleProductSearchStore) {
    store.set('isPersonalisedProductsLoading', true);
    store.set(StoreKeys.responseError, null);
    const isAuthenticated = authService.isLoggedIn() && window.sessionStorage.getItem('authenticatedGuidedFlowEnabled')?.includes('true');
    const productsPromise =
      isAuthenticated && !store.get('addressRequired')
        ? bundleSearchService.getAuthenticatedProducts()
        : bundleSearchService.getProducts(address, null, store.get('couponCode'), store.get('couponCodes'));
    productsPromise
      .then((productDetails) => {
        const bundleProducts: BundleSearchResponse = productDetails as BundleSearchResponse;
        const firstAvailableCategory = bundleSearchService.getFirstAvailableProductCategory(bundleProducts, isAuthenticated);

        if (isBundleLandingPage && firstAvailableCategory !== 'energy') {
          if (firstAvailableCategory === 'nbn') {
            const bundleSearchStore = store.get(StoreKeys.bundleSearchStore);
            bundleSearchStore.energyResponse.siteError = {
              reasonCode: 'serviceNotAvailable',
              description: 'Energy not available, NBN available'
            };
            store.set(StoreKeys.bundleSearchStore, bundleSearchStore);
          } else {
            const bundleSearchStore = store.get(StoreKeys.bundleSearchStore);
            bundleSearchStore.energyResponse.siteError = {
              reasonCode: 'serviceNotAvailable',
              description: 'Energy not available, NBN not available'
            };
            store.set(StoreKeys.bundleSearchStore, bundleSearchStore);
          }
        } else if (store.get(StoreKeys.guidedFlowActiveCategory) == 'none') {
          // As long as energy is available, then will not show error modal and go to bundle builder page
          store.set(StoreKeys.guidedFlowActiveCategory, firstAvailableCategory);
        }

        // ToDo with another ticket
        //Send data to analytics
        /*
      let event = '[Selection Criteria module]';
      const eventData = address.postcode;
      const eventType = `[Fuel Type][Manually Changed]`;
      event = `${event}${eventType}`;
      const data = {
        entered_state: address.state,
        entered_suburb: address.suburb,
        entered_post_code: address.postcode
      };
      tealiumAnalyticService.createAndSendAddressData(['Address Validated'], eventData, data, 'bundle');
      tealiumAnalyticService.trackProductCards([], true, 'bundle');
      */
      })
      .catch((error) => {
        store.set(StoreKeys.responseError, error);
        sessionStorage.setItem(SessionStorageKeys.bundleProducts, null);
        if (error.errorType === 'multipleMatching') {
          store.set('addressRequired', true);
        }
        if (isBundleLandingPage) {
          locationService.navigateToProductBundle();
        }
      })
      .finally(() => {
        store.set('isPersonalisedProductsLoading', false);
        store.set('isPageLoaded', true);
        delayMessageService.stopAllTimers();
      });
    await productsPromise;
  } else {
    const bundleSearchResponse = {
      energyProductsResponse: bundleProductSearchStore.energyResponse,
      nbnProductsResponse: bundleProductSearchStore.nbnResponse,
      mobileProductsResponse: bundleProductSearchStore.mobileResponse
    };
    const firstAvailableCategory = bundleSearchService.getFirstAvailableProductCategory(
      bundleSearchResponse,
      store.get('isAuthenticated') && window.sessionStorage.getItem('authenticatedGuidedFlowEnabled')?.includes('true')
    );
    const guidedFlowActiveCategory = store.get(StoreKeys.guidedFlowActiveCategory);
    const validatedActiveCategory = bundleService.validateActiveCategory(firstAvailableCategory, guidedFlowActiveCategory);
    store.set(StoreKeys.guidedFlowActiveCategory, validatedActiveCategory);
    if (store.get(StoreKeys.guidedFlowActiveCategory) === 'none') {
      sessionStorage.setItem(SessionStorageKeys.bundleProducts, JSON.stringify(bundleProductSearchStore));
    }
    store.set('isPageLoaded', true);
  }
};
